import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import { DataGrid, GridFooter, GridFooterContainer } from "@mui/x-data-grid";
import CustomNoRowsOverlay from "app/components/overlays/CustomNoRowsOverlay";
import { GridLoadingMessageOverlay } from "app/components/overlays/GridLoadingMessageOverlay";
import MDBox from "components/MDBox";
import { FilterAndExportGridToolbar } from "app/components/overlays/FilterAndExportGridToolbar";
import { isEmpty } from "utils/validationUtil";
import { getDateInDDMMYYYYFormat } from "utils/dateUtil";

import "./reports.css";
import "../team-activities/AddActivityForm.css";
import { Button } from "@mui/material";
import {
  Edit,
  NoAction as RemoveCircle,
  Delete as DeleteIcon,
  ContentCopy as ContentCopyIcon,
  LockIcon,
  Disapprove,
} from "components/icons";
import Checkbox from "@mui/material/Checkbox";
import { RowActionsTray } from "app/components/overlays/RowActionsTray";
import { ROLE_ADMIN, ROLE_BDM, ROLE_CLIENT, ROLE_EMPLOYEE_MANAGER, ROLE_EMPLOYEE_MEMBER } from "AppConstants";
import { approveAllActivities, clientApproveActivities, rejectActivity } from "app/services/manager.service";
import { track } from "analytics/usageTracker";
import { FEATURE_APPROVE, FEATURE_CLIENT_APPROVE, FEATURE_REJECT } from "analytics/featureCatalog";
import { toast } from "app/services/toast.service";
import { useDispatch, useSelector } from "react-redux";
import { reportActions } from "store/report-slice";
import { ProfileTooltip } from "../header/Header";
import { ConfirmDialog } from "../ConfirmDialog";
import { useResizableColumnWidths } from "app/hooks/useResizableColumnWidths";

// Checkbox accent colors keyed to each approval state (HRMS-129). The unchecked
// outline and the checked fill both use this color so an enabled checkbox reads as
// "yellow border" (SUBMITTED) / "light blue border" (APPROVED) — a gray checkbox
// means it is disabled/not shown.
const SUBMITTED_CHECKBOX_COLOR = "#e0a800"; // yellow
const APPROVED_CHECKBOX_COLOR = "#0d6efd"; // light blue
// White check mark drawn on top of the checked fill (same asset the app theme uses).
const CHECK_MARK_SVG =
  "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 -1 22 22'%3e%3cpath fill='none' stroke='%23fff' stroke-linecap='round' stroke-linejoin='round' stroke-width='2.5' d='M6 10l3 3l6-6'/%3e%3c/svg%3e\")";
// The app theme (assets/theme/components/form/checkbox.js) renders the checkbox as a CSS
// box: the visible outline is a `border` on .MuiSvgIcon-root (gray by default) and the
// checked state paints a background-image gradient. So per-status coloring must override
// the icon's border-color (empty state) and background/border (checked state). `&&` bumps
// specificity so these win over the theme's colorPrimary overrides. @since NEXT_VERSION
const approvalCheckboxSx = (color) => ({
  "&& .MuiSvgIcon-root": { borderColor: color },
  "&&.Mui-checked .MuiSvgIcon-root": {
    backgroundImage: `${CHECK_MARK_SVG}, linear-gradient(${color}, ${color})`,
    borderColor: color,
  },
});

// Holidays are organization-defined dates with no approval workflow, so they are always
// shown as "APPROVED" (the report proc returns an empty action for them).
// Activities — member-owned AND manager-owned — carry their real approval state from the BE:
// with the two-level manager hierarchy, an L1 manager's own activities are SUBMITTED to their
// L2 (only top-of-chain managers auto-approve), so we must NOT force manager-owned rows to
// "APPROVED". Trust row.approvalStatus, defaulting to SUBMITTED only when it is missing.
export const effectiveApprovalStatus = (row) => {
  if (row?.effortType === "HOLIDAY") return "APPROVED";
  return row?.approvalStatus || "SUBMITTED";
};

// A manager must not approve/reject their OWN activities — those are routed to their L2.
// In the consolidated report a manager-owned row (isMember === false) carries the manager's
// manager_id in `memberId` (the report keys every row by memberId; for manager-owned rows
// member_id = manager_id). So a row whose memberId is one of the viewer's own manager ids
// is "mine". For an L2 viewing their team, an L1's rows carry the L1's id, so they remain
// approvable by the L2.
//
// Every id the viewer holds counts: _manager is unique on (user_id, client_id), so one person
// mapped to four clients owns four manager ids. Matching only one of them let a manager act on
// their own work under every other client.
export const isOwnManagerActivity = (row, currentManagerIds) =>
  row?.effortType === "ACTIVITY" &&
  row?.isMember === false &&
  Array.isArray(currentManagerIds) &&
  currentManagerIds.includes(row?.memberId);

// An activity a manager at the top of the hierarchy logged for themselves: the backend had
// nobody to route it to, so it was approved on creation (HRMS-288). The backend classifies it,
// rather than the grid re-deriving it — a row that does not say so is treated as normally
// approved, so a missing field locks editing rather than opening it. @since NEXT_VERSION
export const isSelfApprovedActivity = (row) => row?.selfApproved === true;

// What the approval column tells a manager about their own activity, once it is one they can
// take no action on themselves. @since NEXT_VERSION
export const ownManagerApprovalHint = (row) => {
  if (row?.hasApprover === false)
    return "You have no reporting manager — an admin must approve this";
  return effectiveApprovalStatus(row) === "APPROVED"
    ? "Approved by your manager"
    : "Awaiting your manager's approval";
};

// Rows eligible to be marked "Approved by Client" (a frozen financial record for invoicing):
// manager-approved ACTIVITY and LEAVE_PLAN rows that are not already client-approved. Holidays
// are excluded — they are shared org-wide dates with no per-member action to persist (see the
// HOL- branch in EffortRepository), so the backend cannot store a per-member client-approval for
// them. Own-manager activities are excluded for the same separation-of-duties reason as approval.
// @since NEXT_VERSION
export const isClientApprovable = (row, currentManagerIds) =>
  (row?.effortType === "ACTIVITY" || row?.effortType === "LEAVE_PLAN") &&
  effectiveApprovalStatus(row) === "APPROVED" &&
  (!isOwnManagerActivity(row, currentManagerIds) || isSelfApprovedActivity(row));

// Sums hoursSpent across rows of a single effortType. Tolerates missing/empty
// arrays and null hoursSpent so the footer never renders NaN.
export const sumHoursByEffortType = (rows, effortType) =>
  (Array.isArray(rows) ? rows : [])
    .filter((r) => r?.effortType === effortType)
    .reduce((sum, r) => sum + (Number(r?.hoursSpent) || 0), 0);

// Hours arrive as Java floats and are then summed in the browser, so a total that is
// mathematically whole lands on 1236.0000000000002 and its complement on 1760.9999999999998
// (HRMS-278). Rounded to a single decimal for display; a genuine half hour still reads 7.5,
// and a whole total keeps its clean form rather than gaining a trailing .0. Returns a number,
// not a string, so React renders it without the trailing zero toFixed would add.
export const formatHours = (value) => {
  const hours = Number(value);
  if (!Number.isFinite(hours)) return 0;
  return Math.round(hours * 10) / 10;
};

// Tallies rows by effective approval status for the summary counts line (HRMS-129).
// Uses effectiveApprovalStatus so holidays count as APPROVED and client-approved rows
// (activities/leaves) count under Approved By Client.
export const countByApprovalStatus = (rows) =>
  (Array.isArray(rows) ? rows : []).reduce(
    (acc, r) => {
      const s = effectiveApprovalStatus(r);
      if (s === "SUBMITTED") acc.submitted += 1;
      else if (s === "APPROVED") acc.approved += 1;
      else if (s === "APPROVED_BY_CLIENT") acc.approvedByClient += 1;
      return acc;
    },
    { submitted: 0, approved: 0, approvedByClient: 0 }
  );

// Footer summary shown beneath the report grid. "Hours worked" counts only
// ACTIVITY rows and "Leave Hours" only LEAVE_PLAN rows (holidays are excluded
// from both). The Shortfall/Overtime line still compares Expected against the
// combined totalHours so leave/holiday time keeps counting toward expected.
// showCounts adds the approval-counts line — only the consolidated report enables it
// (HRMS-129); other consumers of this grid keep the plain hours summary.
export const HoursSummary = ({ reportData, totalHours, expectedWorkingHours, showCounts }) => {
  const workedHours = formatHours(sumHoursByEffortType(reportData, "ACTIVITY"));
  const leaveHours = formatHours(sumHoursByEffortType(reportData, "LEAVE_PLAN"));
  const counts = countByApprovalStatus(reportData);
  // Both sides of the gap are rounded before they are compared, so the Shortfall/Overtime
  // label can never disagree with the figure printed beside it.
  const expectedHours = formatHours(expectedWorkingHours);
  const totalWorkedHours = formatHours(totalHours);
  const gapInHours = formatHours(Math.abs(expectedHours - totalWorkedHours));
  return (
    <div className="report-summary-container">
      <div className="report-summary-item">
        <span className="report-summary-title">Expected Hours:</span>
        <span className="report-summary-value">{expectedHours}</span>
      </div>
      <div className="report-summary-item">
        <span className="report-summary-title">Hours worked:</span>
        <span className="report-summary-value">{workedHours}</span>
      </div>
      <div className="report-summary-item">
        <span className="report-summary-title">Leave Hours:</span>
        <span className="report-summary-value">{leaveHours}</span>
      </div>
      <div className="report-summary-item">
        <span className="report-summary-title">
          {expectedHours > totalWorkedHours ? "Shortfall" : "Overtime"}:
        </span>
        <span
          className={
            "report-summary-value report-summary-value-" +
            (expectedHours > totalWorkedHours ? "less" : "more")
          }
        >
          {gapInHours}
        </span>
      </div>
      {/* Consolidated report only: record counts, labelled and ruled off from the
          hours line above so the tallies are not read as hour values. (HRMS-230) */}
      {showCounts && (
        <div className="report-summary-counts">
          <span className="report-summary-group-label">Records Count:</span>
          <span className="report-summary-item">
            <span className="report-summary-title">Submitted:</span>
            <span className="report-summary-value">{counts.submitted}</span>
          </span>
          <span className="report-summary-item">
            <span className="report-summary-title">Approved:</span>
            <span className="report-summary-value">{counts.approved}</span>
          </span>
          <span className="report-summary-item">
            <span className="report-summary-title">Approved By Client:</span>
            <span className="report-summary-value">{counts.approvedByClient}</span>
          </span>
        </div>
      )}
    </div>
  );
};

// Accepts reportData property from parent
export const ReportDataGridBase = (props) => {
  const dispatch = useDispatch();
  const [pageSize, setPageSize] = useState(20);
  const [selectedRows, setSelectedRows] = useState([]);
  // Rows selected for the "Approved by Client" action — kept separate from the SUBMITTED
  // approval selection so the two footer buttons stay independent. @since NEXT_VERSION
  const [clientSelectedRows, setClientSelectedRows] = useState([]);
  const reportData = useSelector((state) => state.report.rows);
  const activityTypes = useSelector((state) => state.client.typeRows);
  // Controlled row-selection model (v8 shape) so clicking the checkbox/Actions cells can
  // highlight the row like any other cell. @since NEXT_VERSION
  const [rowSelectionModel, setRowSelectionModel] = useState({ type: "include", ids: new Set() });
  // Single confirmation dialog reused for Approve / Approved-by-client / Disapprove,
  // matching the delete-activity confirmation (HRMS-129). onYes holds the pending action.
  const [confirm, setConfirm] = useState({ open: false, title: "", message: "", yesLabel: "Yes", onYes: () => {} });

  const askConfirm = (cfg) => setConfirm({ open: true, yesLabel: "Yes", onYes: () => {}, ...cfg });

  // Same gating as the approval column: only managers (L1/L2) and admins may approve or
  // client-approve. Used to show the Disapprove action inside the row actions tray.
  const canApprove =
    props.displayAppovalBtn === true &&
    (props.role === ROLE_EMPLOYEE_MANAGER || props.role === ROLE_ADMIN);

  // Roles that may look at the timesheet but never change it. A CLIENT has always been one;
  // HRMS-116 point 4 makes a BDM one too -- "They cannot edit, delete or approve activities or
  // leaves for anyone". Approval is already out of reach for both (canApprove names the two roles
  // that have it), so this covers the row actions: edit, copy and delete.
  // @since NEXT_VERSION
  const isReadOnlyRole = props.role === ROLE_CLIENT || props.role === ROLE_BDM;

  // The ids behind each footer action, derived straight from the rows on screen. The
  // client-approvable list also feeds the header "select all". @since NEXT_VERSION
  const { allCheckboxIds, allClientCheckboxIds } = useMemo(() => {
    const submitted = [];
    const clientApprovable = [];
    (Array.isArray(props?.reportData) ? props.reportData : []).forEach((d) => {
      // Submitted activities (for the APPROVE action) — but never the viewer's own.
      if (
        d.effortType === "ACTIVITY" &&
        d.approvalStatus === "SUBMITTED" &&
        !isOwnManagerActivity(d, props?.currentManagerIds)
      )
        submitted.push(d.id);
      // Approved rows (for the APPROVED BY CLIENT action).
      else if (isClientApprovable(d, props?.currentManagerIds)) clientApprovable.push(d.id);
    });
    return { allCheckboxIds: submitted, allClientCheckboxIds: clientApprovable };
  }, [props?.reportData, props?.currentManagerIds]);

  // Applying a filter swaps rows out from under an existing selection. Drop the ids that are
  // no longer actionable so the footer buttons match what is on screen; survivors stay.
  useEffect(() => {
    const submittedIds = new Set(allCheckboxIds);
    const clientApprovableIds = new Set(allClientCheckboxIds);
    // filter() only ever removes, so an unchanged length means unchanged contents —
    // returning prev then keeps the reference stable and skips a needless render.
    setSelectedRows((prev) => {
      const next = prev.filter((id) => submittedIds.has(id));
      return next.length === prev.length ? prev : next;
    });
    setClientSelectedRows((prev) => {
      const next = prev.filter((id) => clientApprovableIds.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [allCheckboxIds, allClientCheckboxIds]);

  // Ticked only while every actionable row is selected. Derived rather than stored so it can
  // never disagree with the selection it describes. @since NEXT_VERSION
  const selectAll =
    allCheckboxIds.length + allClientCheckboxIds.length > 0 &&
    selectedRows.length === allCheckboxIds.length &&
    clientSelectedRows.length === allClientCheckboxIds.length;

  let columns = [
    {
      field: "approvalHeader",
      headerName: "",
      renderHeader: () => <Checkbox onChange={(e) => selectAllHandler(e)} checked={selectAll} />,
      width: 60,
      align: "center",
      headerAlign: "center",
      sortable: false, // get
      dropDownMenu: false,
      filters: false,
      filterable: false,
      type: "actions",
      getActions: (obj) => {
        // Client-approved rows are frozen financial records — no control, just a lock indicator.
        if (effectiveApprovalStatus(obj.row) === "APPROVED_BY_CLIENT")
          return [
            <ProfileTooltip title="Approved by Client — locked">
              <LockIcon color="disabled" />
            </ProfileTooltip>,
          ];
        // A manager cannot approve/reject their own activities — they are actioned by the L2.
        // Their own self-approved work is the exception: nobody else can ever mark it approved
        // by the client, so it falls through to that checkbox instead (HRMS-288).
        if (
          isOwnManagerActivity(obj.row, props?.currentManagerIds) &&
          !isClientApprovable(obj.row, props?.currentManagerIds)
        )
          return [
            <ProfileTooltip title={ownManagerApprovalHint(obj.row)}>
              <RemoveCircle color="disabled" />
            </ProfileTooltip>,
          ];
        // Submitted activities: yellow manager-approval checkbox (feeds the "APPROVE" button).
        if (obj.row.effortType === "ACTIVITY" && obj.row.approvalStatus === "SUBMITTED")
          return [
            <Checkbox
              value={obj.row.id}
              sx={approvalCheckboxSx(SUBMITTED_CHECKBOX_COLOR)}
              onChange={(e) => {
                e.target.checked === true
                  ? setSelectedRows([...selectedRows, obj.row.id])
                  : setSelectedRows(selectedRows.filter((i) => i !== obj.row.id));
              }}
              checked={selectedRows.includes(obj.row.id) === true ? true : false}
            />,
          ];
        // Approved activities & leaves: light-blue client-approval checkbox (feeds the
        // "APPROVED BY CLIENT" button). Colored differently from the yellow SUBMITTED
        // checkbox above so the two selection states read distinctly.
        if (isClientApprovable(obj.row, props?.currentManagerIds))
          return [
            <Checkbox
              value={obj.row.id}
              className="client-approve-checkbox"
              sx={approvalCheckboxSx(APPROVED_CHECKBOX_COLOR)}
              onChange={(e) => {
                e.target.checked === true
                  ? setClientSelectedRows([...clientSelectedRows, obj.row.id])
                  : setClientSelectedRows(clientSelectedRows.filter((i) => i !== obj.row.id));
              }}
              checked={clientSelectedRows.includes(obj.row.id) === true ? true : false}
            />,
          ];
        // Anything else (e.g. default-approved holidays) has no control here; its
        // "Default Approved" indicator now lives in the row actions tray.
        return [];
      },
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      cellClassName: "actions",
      getActions: (obj) => {
        const status = effectiveApprovalStatus(obj.row);
        // Leaves are auto-approved at creation (intentional), so they would never
        // satisfy the SUBMITTED check below. Allow leaves to be edited/deleted
        // regardless of approval status; activities still require SUBMITTED. A
        // client-approved row is a frozen record and can never be edited/deleted.
        // A self-approved activity is editable too: no one else approved it, so no one else's
        // decision is being undone (HRMS-288).
        const canEdit =
          status !== "APPROVED_BY_CLIENT" &&
          (obj.row.approvalStatus === "SUBMITTED" ||
            obj.row.effortType === "LEAVE_PLAN" ||
            isSelfApprovedActivity(obj.row)) &&
          obj.row.effortType !== "HOLIDAY" &&
          !isReadOnlyRole;

        // An availed optional holiday is the owner's own election, so they can give it back.
        // Whether it is still inside the backdating window is the server's decision, arriving as
        // optionalHolidayRemovable (HRMS-281): the window is a backend property measured against
        // "today" in the business zone, so a browser recomputing it would disagree across midnight
        // and after any config change. A row that does not say so is treated as not removable.
        const canRemoveOptionalHoliday =
          obj.row.effortType === "HOLIDAY" &&
          obj.row.optionalHoliday === true &&
          // HRMS-281: the backdating window is the server's decision, arriving as this flag.
          obj.row.optionalHolidayRemovable === true &&
          // HRMS-116: a BDM is read-only too, so it is no longer just the client role here.
          !isReadOnlyRole;

        const actions = [];

        if (canEdit)
          actions.push({
            key: "edit",
            label: "Edit",
            color: "inherit",
            icon: <Edit fontSize="small" />,
            onClick: () => props.editEventHandler(obj.row),
          });

        // Copy is available on every activity row, regardless of approval status
        // (it creates a new SUBMITTED row and never mutates the frozen source).
        if (obj.row.effortType === "ACTIVITY" && !isReadOnlyRole && props.copyEventHandler)
          actions.push({
            key: "copy",
            label: "Copy",
            color: "inherit",
            icon: <ContentCopyIcon sx={{ fontSize: 18 }} />,
            onClick: () => props.copyEventHandler(obj.row),
          });

        if (canEdit || canRemoveOptionalHoliday)
          actions.push({
            key: "delete",
            label: "Delete",
            color: "error",
            icon: <DeleteIcon fontSize="small" />,
            onClick: () => props.confirmDeleteDialogHandler(obj.row.effortType, obj.row),
          });

        // Disapprove (moved out of the approval column): send an approved activity back to
        // SUBMITTED. Only approvers, only ACTIVITY rows that are approved-but-not-client-approved.
        // Never the viewer's own — the same separation of duties the approval column enforces,
        // and it is what kept the "disapprove, then edit" workaround alive (HRMS-288).
        if (
          canApprove &&
          obj.row.effortType === "ACTIVITY" &&
          status === "APPROVED" &&
          !isOwnManagerActivity(obj.row, props?.currentManagerIds) &&
          !isSelfApprovedActivity(obj.row)
        )
          actions.push({
            key: "disapprove",
            label: "Disapprove",
            color: "error",
            icon: <Disapprove fontSize="small" />,
            onClick: () => confirmDisapprove(obj.row),
          });

        // Default Approved (moved out of the approval column): info-only indicator for rows
        // that carry default manager approval — holidays (always) and auto-approved leaves.
        // Dropped once a leave is client-approved (status flips to APPROVED_BY_CLIENT).
        if (
          obj.row.effortType === "HOLIDAY" ||
          (obj.row.effortType === "LEAVE_PLAN" && status === "APPROVED")
        )
          actions.push({
            key: "default-approved",
            label: "Default Approved",
            color: "secondary",
            icon: <RemoveCircle fontSize="small" />,
            disabled: true,
          });

        return [<RowActionsTray actions={actions} />];
      }, // getAction
    },
    {
      field: "memberName",
      headerName: "Member",
      width: 200,
    },
    {
      field: "hoursSpent",
      headerName: "Hours",
      width: 90,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "startDate",
      headerName: "Start Date",
      width: 110,
      valueGetter: (value, row) => getDateInDDMMYYYYFormat(row.startDate),
    },
    {
      field: "endDate",
      headerName: "End Date",
      width: 110,
      valueGetter: (value, row) => getDateInDDMMYYYYFormat(row.endDate),
    },
    {
      field: "approvalStatus",
      headerName: "Approval Status",
      width: 160,
      // Display is human-readable (APPROVED BY CLIENT); the cell class keeps the raw
      // underscored status so the color rules (approval-status--approved_by_client) match.
      valueGetter: (value, row) => effectiveApprovalStatus(row).replace(/_/g, " "),
      cellClassName: (params) => {
        const status = effectiveApprovalStatus(params.row);
        return `approval-status--${status.toLowerCase()}`;
      },
    },
    // Project comes resolved from the backend (joined in EffortRepositoryCustomImpl), so there is
    // no client-side lookup here. Blank for leave, holidays, and activities with no project.
    // @since NEXT_VERSION
    { field: "projectCode", headerName: "Project Code", width: 130 },
    { field: "clientTicketRef", headerName: "Task Ticket / Details", width: 150 },
    {
      field: "client",
      headerName: "Client",
      width: 150,
      valueGetter: (value, row) =>
        props.clientLabelById?.[String(row.clientId)] ?? (row.clientId ?? ""),
    },
    {
      field: "effortType",
      headerName: "Type",
      width: 100,
      valueGetter: (value, row) =>
        row.effortType === "LEAVE_PLAN" ? "LEAVE" : row.effortType,
    },
    {
      field: "subType",
      headerName: "Sub-Type",
      width: 150,
      valueGetter: (value, row) => {
        if (row.effortType === "ACTIVITY") {
          const match = activityTypes?.find((t) => t?.id === row.activityTypeId);
          return match?.activityDesc ?? "";
        }
        if (row.effortType === "LEAVE_PLAN") {
          // The consolidated report endpoint doesn't carry leave_duration, but the proc returns
          // half-day leave as a fractional hours value (4.5) and full-day leave as whole-hour
          // multiples (9, 18, …). Derive the sub-type from that.
          return Number.isInteger(row.hoursSpent) ? "FULL-DAY" : "HALF-DAY";
        }
        if (row.effortType === "HOLIDAY") return "HOLIDAY";
        return "";
      },
    },
    {
      field: "status",
      headerName: "Status",
      width: 150,
      valueGetter: (value, row) => row.status.replace("_", " "),
    },
    { field: "description", headerName: "Description/Reason", width: 400 },
  ];

  if (!canApprove) {
    columns = columns.filter((column) => {
      return column.field !== "approvalHeader";
    });
  }

  // Drop the whole Actions column rather than render an empty tray on every row.
  if (isReadOnlyRole) {
    columns = columns.filter((column) => {
      return column.field !== "actions";
    });
  }

  if (props.role === ROLE_EMPLOYEE_MEMBER || props.hideMemberColumn === true) {
    columns = columns.filter((column) => {
      return column.field !== "memberName";
    });
  }

  // Keeps a dragged column width alive through the re-render that any click in the grid
  // causes. Must stay above the "Data Loading" early return so the hook always runs.
  const { columns: sizedColumns, onColumnWidthChange } = useResizableColumnWidths(columns);

  // Header "select all" selects every actionable checkbox in the grid: the SUBMITTED
  // rows (APPROVE) and the approved rows (APPROVED BY CLIENT).
  const selectAllHandler = (e) => {
    if (e.target.checked) {
      setSelectedRows(allCheckboxIds);
      setClientSelectedRows(allClientCheckboxIds);
    } else {
      setSelectedRows([]);
      setClientSelectedRows([]);
    }
  };

  // Skipped rows can be leaves as well as activities, so they are counted as records.
  const recordCountLabel = (n) => `${n} ${n === 1 ? "record" : "records"}`;

  // The backend skips records whose status moved on since they were ticked, and tells us how
  // many — not which — so reload rather than paint a status that was never saved. @since NEXT_VERSION
  const reportSkippedRecords = (response, pastTenseAction) => {
    const skipped = response?.data?.skipped ?? 0;
    if (skipped <= 0) return false;
    toast.warning(
      `${recordCountLabel(skipped)} could not be ${pastTenseAction} because the approval status had already changed. The report has been refreshed.`
    );
    props.refreshDataHandler?.();
    return true;
  };

  const approveHandler = () => {
    approveAllActivities(selectedRows).then((response) => {
      track(FEATURE_APPROVE);
      // Approved rows are no longer selectable, so clear the selection to hide
      // the Approve button (it only shows while rows remain selected).
      setSelectedRows([]);
      if (reportSkippedRecords(response, "approved")) return;
      const updatedData = reportData.map((x) =>
        selectedRows.includes(x.id) ? { ...x, approvalStatus: "APPROVED" } : x
      );
      dispatch(reportActions.replaceData(updatedData));
    });
  };

  const rejectHandler = (id) => {
    rejectActivity([id]).then(() => {
      track(FEATURE_REJECT);
      const updatedData = [...reportData];
      const index = updatedData.findIndex((x) => x.id === id);
      const obj = { ...updatedData[index] };
      obj.approvalStatus = "SUBMITTED";
      updatedData[index] = obj;
      dispatch(reportActions.replaceData(updatedData));
    });
  };

  // Marks the selected approved rows as APPROVED_BY_CLIENT — a frozen financial record.
  // Once set, these rows expose no UI control (guarded again on the backend). @since NEXT_VERSION
  const clientApproveHandler = () => {
    clientApproveActivities(clientSelectedRows).then((response) => {
      track(FEATURE_CLIENT_APPROVE);
      setClientSelectedRows([]);
      if (reportSkippedRecords(response, "marked as approved by client")) return;
      const updatedData = reportData.map((x) =>
        clientSelectedRows.includes(x.id) ? { ...x, approvalStatus: "APPROVED_BY_CLIENT" } : x
      );
      dispatch(reportActions.replaceData(updatedData));
    });
  };

  // Small helper: "N activity"/"N activities".
  const activityCountLabel = (n) => `${n} ${n === 1 ? "activity" : "activities"}`;

  // Each button/action opens the shared ConfirmDialog first; the real work runs on confirm.
  const confirmApprove = () =>
    askConfirm({
      title: "Approve Activities?",
      message: `Do you want to approve selected ${activityCountLabel(selectedRows.length)}?`,
      yesLabel: "Approve",
      onYes: approveHandler,
    });

  const confirmClientApprove = () =>
    askConfirm({
      title: "Approved by Client?",
      message: `Do you want to mark selected ${activityCountLabel(clientSelectedRows.length)} as APPROVED BY CLIENT?`,
      yesLabel: "Approved by Client",
      onYes: clientApproveHandler,
    });

  // Disapprove confirmation mirrors the delete-activity confirmation wording/format.
  const confirmDisapprove = (row) =>
    askConfirm({
      title: "Disapprove Activity?",
      message: `Are you sure you want to disapprove this Activity (Start Date: '${row.startDate}', Hours: '${row.hoursSpent}', Description: '${row.description?.substring(0, 15)}...')?`,
      yesLabel: "Disapprove",
      onYes: () => rejectHandler(row.id),
    });

  const summaryProps = {
    reportData: props.reportData,
    totalHours: props.totalHours,
    expectedWorkingHours: props.expectedWorkingHours,
    showCounts: props.showApprovalCounts === true,
  };

  const ApprovalFooterComponent = () => {
    return (
      <GridFooterContainer>
        <div style={{ display: "flex", alignItems: "center" }}>
          <HoursSummary {...summaryProps} />
          {selectedRows.length > 0 && (
            <Button
              // flexShrink/nowrap keep the footer buttons a consistent single-line
              // height on narrow screens (otherwise the longer label wraps and grows).
              sx={{ ml: "10px", flexShrink: 0, whiteSpace: "nowrap" }}
              variant="contained"
              color="success"
              onClick={confirmApprove}
            >
              APPROVE ({selectedRows.length})
            </Button>
          )}
          {clientSelectedRows.length > 0 && (
            <Button
              sx={{ ml: "10px", flexShrink: 0, whiteSpace: "nowrap" }}
              variant="contained"
              color="secondary"
              onClick={confirmClientApprove}
            >
              APPROVED BY CLIENT ({clientSelectedRows.length})
            </Button>
          )}
        </div>
        <GridFooter
          sx={{
            border: "none", // To delete double border.
          }}
        />
      </GridFooterContainer>
    );
  };

  const CustomFooterTotalComponent = () => {
    return <HoursSummary {...summaryProps} />;
  };

  const getData = () => {
    if (isEmpty(props?.reportData) || props?.reportData?.length === 0) return [];
    else {
      return props?.reportData;
    }
  };

  if (isEmpty(props?.reportData)) {
    return (
      <React.Fragment>
        <div>Data Loading</div>
      </React.Fragment>
    );
  }

  return (
    <>
      <MDBox py={1.5} px={2} height={500}>
        <DataGrid
          disableColumnMenu
          density="compact"
          rows={getData()}
          columns={sizedColumns}
          onColumnWidthChange={onColumnWidthChange}
          pageSize={pageSize}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          rowsPerPageOptions={[5, 10, 20]}
          // Consolidated report only: the footer holds a two-line summary (hours +
          // approval counts) plus the approve buttons, so reserve extra height; otherwise
          // it overlaps the last rows when the grid is full. The row area shrinks to make
          // room. Other grids keep the default footer height. (HRMS-129)
          sx={
            props.showApprovalCounts === true
              ? { "& .MuiDataGrid-footerContainer": { minHeight: 92, alignItems: "center" } }
              : undefined
          }
          // Remove the "N row selected" footer text; keep the row highlight itself.
          hideFooterSelectedRowCount
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={(model) => setRowSelectionModel(model)}
          // The checkbox/Actions cells don't select the row by default — do it explicitly
          // so clicking them highlights the row like clicking any other cell.
          onCellClick={(params) => {
            if (params.field === "approvalHeader" || params.field === "actions") {
              setRowSelectionModel({ type: "include", ids: new Set([params.id]) });
            }
          }}
          // While the parent fetches, say so instead of showing "No rows" over an empty grid.
          // @since NEXT_VERSION
          loading={props.loading === true}
          slots={
            props.memberFlag === true
              ? {
                  footer: ApprovalFooterComponent,
                  loadingOverlay: GridLoadingMessageOverlay,
                }
              : {
                  toolbar: FilterAndExportGridToolbar,
                  loadingOverlay: GridLoadingMessageOverlay,
                  noRowsOverlay: CustomNoRowsOverlay,
                  footer: CustomFooterTotalComponent,
                }
          }
          slotProps={{ loadingOverlay: { message: "Loading consolidated report..." } }}
        />
      </MDBox>
      <ConfirmDialog
        title={confirm.title}
        open={confirm.open}
        setOpen={(v) => setConfirm((c) => ({ ...c, open: v }))}
        onConfirm={confirm.onYes}
        yesButtonLabel={confirm.yesLabel}
        noButtonLabel="Cancel"
      >
        {confirm.message}
      </ConfirmDialog>
    </>
  );
};

ReportDataGridBase.propTypes = {
  reportData: PropTypes.array.isRequired,
  // True while the rows are being fetched. @since NEXT_VERSION
  loading: PropTypes.bool,
  totalHours: PropTypes.number.isRequired,
  expectedWorkingHours: PropTypes.number.isRequired,
  clientLabelById: PropTypes.object,
};

HoursSummary.propTypes = {
  reportData: PropTypes.array,
  totalHours: PropTypes.number,
  expectedWorkingHours: PropTypes.number,
  showCounts: PropTypes.bool,
};
